import { LoadOptions } from 'devextreme/data';
import { NewColumn, PagingOptions, RemoteOperation } from '../datagrid-types';
import {
  CellClickEvent,
  CellDblClickEvent,
  DataGridScrollMode,
  RowClickEvent,
  RowDblClickEvent
} from 'devextreme/ui/data_grid_types';
import dxDataGrid, { Column } from 'devextreme/ui/data_grid';
import { Theme } from '@mui/material';

/**
 * Calculates the current page number based on the provided load options, total number of items, and page size.
 * @param loadOptions - The load options object containing the current skip value.
 * @param totalItems - The total number of items in the data set.
 * @param pageSize - The number of items to display per page.
 * @returns The current page number.
 */
export const calculatePageNumber = (loadOptions: LoadOptions<any>, totalItems: number, pageSize: number): number => {
  const { skip = 0 } = loadOptions;

  if (skip < pageSize || totalItems === 0 || pageSize === 0) {
    return 0;
  } else {
    return Math.floor(skip / pageSize);
  }
};

const getColumnType = (columnName: string, columns: Column<any, any>[]) => {
  const type = columns.find(col => col.dataField === columnName)?.dataType;
  if (type) {
    return type;
  } else {
    return 'string';
  }
};

/**
 * Parses a filter object and returns a string representation of the filter.
 * @param filter - The filter object to parse.
 * @returns A string representation of the filter.
 */
const parse = (filter: any, columnName: string, columns: Column<any, any>[]) => {
  const ruleConversion = (rule: string, value: string) => {
    const formattedValue =
      getColumnType(columnName, columns) === 'boolean' || getColumnType(columnName, columns) === 'number'
        ? value
        : `'${value}'`;

    switch (rule) {
      case 'contains':
        return `li '%${value}%'`;
      case 'equals':
      case '=':
        return `eq ${formattedValue}`;
      case '<>':
        return `ne ${formattedValue}`;
      case 'notcontains':
        return `ne '%${value}%'`;
      case 'endswith':
        return `li '%${value}'`;
      case 'startswith':
        return `li '${value}%'`;
      default:
        return `li '%${value}%'`;
    }
  };

  const filterStrings = [];

  if (typeof filter?.[0] === 'string') {
    const [propertyName, condition, value] = filter;

    if (value !== undefined && value !== null) {
      filterStrings.push(`${propertyName} ${ruleConversion(condition, value)}`);
    }
  }

  if (Array.isArray(filter?.[0])) {
    filter.forEach((filterMember: string | any[]) => {
      if (typeof filterMember === 'string') {
        filterStrings.push(filterMember);
      }
      if (Array.isArray(filterMember)) {
        if (typeof filterMember[0] === 'string') {
          const [propertyName, condition, value] = filterMember;

          if (value !== undefined && value !== null) {
            filterStrings.push(`${propertyName} ${ruleConversion(condition, value)}`);
          }
        }
      }
    });
  }

  return filterStrings.join(' ');
};

/**
 * Parses the filter groups and returns a string representation of the filter.
 * @param filter - The filter to parse.
 * @param columns - The columns to use for parsing the filter.
 * @returns A string representation of the filter.
 */
const parseFilterGroups = (filter: any[], columns: Column<any, any>[], excludedSearchProperties: string[] = []) => {
  const sysColumnsList = [
    'masterDataId',
    'id',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
    'isDeleted',
    'deletedBy',
    'deletedOn',
    'ownerTenantId',
    'ownerSiteId',
    'version',
    'serviceLocation',
    'ownerPartnerId',
    'endPoint',
    'attachmentCount',
    'isGlobal',
    'isActive',
    'isDefault',
    'isVisible',
    'isEditable',
    'isUnique',
    'isNullable',
    'sequence',
    'actions',
    'saleOrderType',
    'latestEta',
    'customerId'
  ].concat(excludedSearchProperties);

  const filterStrings: string[] = [];

  // Handle negation: when users uncheck multiple header filter options, DevExtreme sends ["!", [...]]
  // We convert "NOT (A or B)" to OData syntax: "A ne 'val1' and B ne 'val2'"
  if (filter[0] === '!' && Array.isArray(filter[1])) {
    const negatedFilters = filter[1]
      // Filter arrays are structured as: [fieldName, operator, value]
      .filter((item: any) => Array.isArray(item) && item.length === 3)
      .map((item: any) => {
        const [fieldName, , value] = item;

        return value !== undefined && value !== null ? `${fieldName} ne '${value}'` : null;
      })
      .filter((filter: string | null) => filter !== null);

    return negatedFilters.length > 0 ? negatedFilters.join(' and ') : '';
  }

  filterStrings.push('(');

  if (typeof filter?.[0] === 'string') {
    filterStrings.push(`${parse(filter, filter[0], columns)}`);
  } else {
    let added = false;
    for (let index = 0; index < filter.length; index++) {
      const filterMember = filter[index];
      if (Array.isArray(filterMember) && typeof filterMember?.[0] === 'string') {
        const column = columns.find(col => col.dataField === filterMember[0]);
        if (
          sysColumnsList.includes(filterMember[0]) ||
          column?.dataType !== 'string' ||
          filterMember[0].toLowerCase().includes('time') ||
          filterMember[0].toLowerCase().includes('date') ||
          !filterMember[2]
        ) {
          // Skip the next iteration
          index++;
          continue;
        } else {
          filterStrings.push(`${parse(filterMember, filterMember[0], columns)}`);
          added = true;
        }
      }
      if ((added || index === 1) && typeof filterMember === 'string') {
        filterStrings.push(filterMember);
        added = false;
      }
      if (Array.isArray(filterMember) && Array.isArray(filterMember[0])) {
        filterStrings.push(parseFilterGroups(filterMember, columns));
      }
    }
  }

  // Remove trailing 'or' or 'and' operators from the filter string before closing parenthesis
  while (filterStrings.length > 0) {
    const lastItem = filterStrings[filterStrings.length - 1].trim().toLowerCase();
    if (lastItem === 'or' || lastItem === 'and') {
      filterStrings.pop();
    } else {
      break;
    }
  }

  filterStrings.push(')');

  return filterStrings.join(' ');
};

const getSearchableFields = (searchExpression: unknown, columns: Column<any, any>[]): string[] => {
  const fields = Array.isArray(searchExpression) ? searchExpression : [searchExpression];

  return fields
    .filter((field): field is string => typeof field === 'string')
    .filter(field => columns.some(column => column.dataField === field && column.dataType === 'string'));
};

const buildSearchPredicate = (
  searchExpression: unknown,
  searchOperation: string,
  searchValue: unknown,
  columns: Column<any, any>[]
): string => {
  if (searchValue === undefined || searchValue === null || searchValue === '') return '';

  const searchableFields = getSearchableFields(searchExpression, columns);
  const escapedSearchValue = typeof searchValue === 'string' ? searchValue.replace(/'/g, "''") : searchValue;
  const predicates = searchableFields.map(field => parse([field, searchOperation, escapedSearchValue], field, columns));

  return predicates.length > 0 ? `(${predicates.join(' or ')})` : '';
};

/**
 * Generates a filter string based on the provided load options and columns.
 * @param loadOptions - The load options to generate the filter string from.
 * @param columns - The columns to use for parsing the filter groups.
 * @returns The generated filter string.
 */
export const generateFilterString = (
  loadOptions: LoadOptions<any>,
  columns: Column<any, any>[],
  ignoreDates: boolean,
  excludedSearchProperties: string[] = []
) => {
  const { filter = [] as string[] | string[][], searchExpr, searchOperation = 'contains', searchValue } = loadOptions;

  // Unpaged loads (e.g. header filter value lookups) must not add grid filters.
  if (!loadOptions.skip && !loadOptions.take) {
    return '';
  }

  const filterPredicate = filter.length > 0 ? parseFilterGroups(filter, columns, excludedSearchProperties) : '';
  const searchPredicate = buildSearchPredicate(searchExpr, searchOperation, searchValue, columns);

  return combineFilterStrings(filterPredicate, searchPredicate);
};

export const combineFilterStrings = (...filters: Array<string | null | undefined>): string => {
  const predicates = filters
    .map(filter => filter?.trim().replace(/^(and|or)\s+/i, ''))
    // Empty groups such as '( )' are rejected by the API with a 500.
    .filter((filter): filter is string => !!filter && !/^[()\s]*$/.test(filter));

  return [...new Set(predicates)].join(' and ');
};

/**
 * Generates a string representation of the sorting options for a data grid.
 * @param loadOptions - The load options for the data grid.
 * @returns A string representation of the sorting options.
 */
export const generateSortString = (loadOptions: LoadOptions<any>) => {
  const { sort = [] } = loadOptions;

  return Array.isArray(sort) && sort[0]
    ? `${(sort[0] as { selector: string; desc: boolean })?.selector ?? ''}:${
        (sort[0] as { selector: string; desc: boolean }).desc ? 'desc' : 'asc'
      }`
    : '';
};

/**
 * Determines the remote operations to be performed based on the provided options.
 * @param remoteOperations - The remote operations to be performed.
 * @param pagingOptions - The paging options to be used.
 * @returns The remote operation to be performed.
 */
export const determineRemoteOperations = (
  remoteOperations: RemoteOperation,
  _pagingOptions: PagingOptions
): RemoteOperation => {
  if (remoteOperations) return remoteOperations;

  // if (pagingOptions?.enable && (pagingOptions as PagingOptionsActive)?.scrollMode === 'standard') {
  //   return {
  //     paging: true,
  //     filtering: false,
  //     sorting: false,
  //     grouping: false,
  //     summary: false
  //   };
  // }

  return undefined;
};

/**
 * Determines the scroll mode for the data grid based on the provided scroll mode and paging options.
 * @param scrollMode The scroll mode to use, if provided.
 * @param pagingOptions The paging options to use, if paging is enabled.
 * @returns The determined scroll mode for the data grid.
 */
export const determinScrollMode = (
  scrollMode: DataGridScrollMode | undefined,
  pagingOptions: PagingOptions
): DataGridScrollMode => {
  if (scrollMode) return scrollMode;

  if (pagingOptions?.enable && 'scrollMode' in pagingOptions) {
    return pagingOptions?.scrollMode;
  }

  return 'infinite';
};

/**
 * Returns the text to be displayed in a chip cell based on the given column and value.
 * If the column has a custom `renderCellText` function defined in its `chipOptions`, that function will be used to generate the text.
 * Otherwise, the `label` property of the column's `chipOptions` will be used.
 * @param column - The column configuration object.
 * @param value - The value to be displayed in the chip cell.
 * @returns The text to be displayed in the chip cell.
 */
export const handleChipText = (column: NewColumn, value: any) => {
  return column.chipOptions?.renderCellText ? column.chipOptions?.renderCellText?.(value) : column.chipOptions?.label;
};

/**
 * Returns the color for a chip based on the value of a given column in a row.
 * @param col - The column to get the chip color for.
 * @param row - The row containing the data for the column.
 * @returns The color for the chip.
 */
export const handleChipColor = (col: NewColumn, row: { [x: string]: any }) => {
  return col.chipOptions?.color?.(row[col?.dataField ?? '']);
};

/**
 * Returns the custom color for a chip based on the provided column and row data.
 * @param col - The column object containing the chip options.
 * @param row - The row data object containing the value for the column's data field.
 * @returns The custom color for the chip, if it exists.
 */
export const handleCustomChipColor = (col: NewColumn, row: { [x: string]: any }) => {
  return col.chipOptions?.customColorOverride?.(row[col?.dataField ?? '']);
};

/**
 * Handles a single click event on a row in a data grid.
 *
 * @param e - The row click event.
 * @param rowClickAllowed - A boolean indicating whether row click is allowed.
 * @param handler - An optional function to handle the row click event.
 */
export const handleRowSingleClick = (
  e: RowClickEvent,
  disableSelectionOnClick: boolean,
  handler?: (e: RowClickEvent) => void
) => {
  e.event?.stopPropagation();

  if (!disableSelectionOnClick && typeof handler === 'function') {
    handler(e);
  }
};

/**
 * Handles a double click event on a row, if row click is allowed and a handler function is provided.
 * @param e - The row double click event.
 * @param rowClickAllowed - A boolean indicating whether row click is allowed.
 * @param handler - An optional function to handle the row double click event.
 */
export const handleRowDoubleClick = (
  e: RowDblClickEvent,
  disableSelectionOnClick: boolean,
  handler?: (e: RowDblClickEvent) => void
) => {
  e.event?.stopPropagation();
  if (!disableSelectionOnClick && typeof handler === 'function') {
    handler(e);
  }
};

/**
 * Handles a single click event on a cell.
 * @param e The CellClickEvent object.
 * @param handler An optional function to handle the event.
 */
export const handleCellSingleClick = (e: CellClickEvent, handler?: (e: CellClickEvent) => void) => {
  e.event?.stopPropagation();
  if (typeof handler === 'function') {
    handler(e);
  }
};

/**
 * Handles the double click event on a cell.
 * @param e The CellDblClickEvent object.
 * @param handler An optional function to handle the event.
 */
export const handleCellDoubleClick = (e: CellDblClickEvent, handler?: (e: CellDblClickEvent) => void) => {
  e.event?.stopPropagation();
  if (typeof handler === 'function') {
    handler(e);
  }
};

/**
 * Checks if the given array of columns has a column with a display order.
 * @param columns - The array of columns to check.
 * @param toCamelCase - An optional function to convert column names to camel case before checking.
 * @returns True if a column with a display order is found, false otherwise.
 */
export const hasDisplayOrder = (columns: any[], toCamelCase?: (str: string | undefined) => string): boolean => {
  for (let columnsIndex = 0; columnsIndex < columns.length; columnsIndex++) {
    const element = columns[columnsIndex];

    if (toCamelCase?.(element.field) === 'displayOrder' || toCamelCase?.(element.name) === 'displayOrder') {
      return true;
    }
  }

  return false;
};

/**
 * Checks if any of the columns have a field or name property with the value 'code'.
 * @param columns - An array of objects representing columns.
 * @param toCamelCase - An optional function to convert strings to camelCase.
 * @returns A boolean indicating whether any of the columns have a 'code' field or name.
 */
export const hasCode = (columns: any[], toCamelCase?: (str: string | undefined) => string): boolean => {
  for (let columnsIndex = 0; columnsIndex < columns.length; columnsIndex++) {
    const element = columns[columnsIndex];

    if (toCamelCase?.(element.field) === 'code' || toCamelCase?.(element.name) === 'code') {
      return true;
    }
  }

  return false;
};

/**
 * Applies a custom style to the selected rows in a dxDataGrid component based on the current theme.
 * @param grid The dxDataGrid component instance.
 * @param selectedRowKeys An array of keys representing the selected rows.
 * @param theme The current theme object.
 */
export const handleSelectedRowStyle = (
  grid: dxDataGrid<any, any> | undefined,
  selectedRowKeys: any[],
  theme: Theme
) => {
  selectedRowKeys.forEach((key: any) => {
    const rowIndex = grid?.getRowIndexByKey(key);
    const rowElement = grid?.getRowElement(rowIndex ?? -1);

    if (rowElement?.[0]) {
      const rowHtmlElement = rowElement[0] as HTMLElement;
      if (theme.palette.mode === 'light') {
        // Store the original styles
        rowHtmlElement.dataset.originalStyle = rowHtmlElement.style.cssText;
        // Apply the new styles
        // rowHtmlElement.style.backgroundColor = theme.palette.customColors.primaryLightBackground;
        // Apply the new styles
        rowHtmlElement.childNodes.forEach((node: any) => {
          if (!node.classList?.contains('dx-command-select')) {
            node.dataset.originalStyle = node.style.cssText;
            node.style.backgroundColor = theme.palette.customColors.primaryLightBackground;
          }
        });
      } else {
        // Store the original styles
        rowHtmlElement.dataset.originalStyle = rowHtmlElement.style.cssText;

        // Apply the new styles
        rowHtmlElement.childNodes.forEach((node: any) => {
          if (!node.classList?.contains('dx-command-select')) {
            node.dataset.originalStyle = node.style.cssText;
            node.style.backgroundColor = theme.palette.customColors.darkBg;
          }
        });
      }
    }
    if (rowElement?.[1]) {
      const rowHtmlElement = rowElement[1] as HTMLElement;
      if (theme.palette.mode === 'light') {
        // Store the original styles
        rowHtmlElement.dataset.originalStyle = rowHtmlElement.style.cssText;
        // Apply the new styles
        // rowHtmlElement.style.backgroundColor = theme.palette.customColors.primaryLightBackground;
        rowHtmlElement.childNodes.forEach((node: any) => {
          if (!node.classList?.contains('dx-command-select')) {
            node.dataset.originalStyle = node.style.cssText;
            node.style.backgroundColor = theme.palette.customColors.primaryLightBackground;
          }
        });
      } else {
        // Store the original styles
        rowHtmlElement.dataset.originalStyle = rowHtmlElement.style.cssText;
        // Apply the new styles
        rowHtmlElement.childNodes.forEach((node: any) => {
          if (!node.classList?.contains('dx-command-select')) {
            node.dataset.originalStyle = node.style.cssText;
            node.style.backgroundColor = theme.palette.customColors.darkBg;
          }
        });
      }

      const checkboxContainer = rowHtmlElement.querySelector('div.dx-checkbox');

      if (checkboxContainer && checkboxContainer.classList.contains('dx-checkbox-checked')) {
        const checkbox = checkboxContainer.children[1].firstChild as HTMLElement;
        checkbox.dataset.originalStyle = checkbox.style.cssText;
        checkbox.style.backgroundColor = theme.palette.customColors.primaryGradient;
      }
    }
  });
};

/**
 * Reapplies the original styles to the deselected rows in a dxDataGrid.
 * @param grid - The dxDataGrid instance.
 * @param currentDeselectedRowKeys - An array of keys for the currently deselected rows.
 */
export const handleDeselectedRowStyle = (grid: dxDataGrid<any, any> | undefined, currentDeselectedRowKeys: any[]) => {
  currentDeselectedRowKeys.forEach((key: any) => {
    const rowIndex = grid?.getRowIndexByKey(key);
    const rowElement = grid?.getRowElement(rowIndex ?? -1);
    if (rowElement?.[0]) {
      const rowHtmlElement = rowElement[0] as HTMLElement;

      // Reapply the original styles
      rowHtmlElement.style.cssText = '';
      rowHtmlElement?.childNodes?.forEach((node: any) => {
        if (!node.classList?.contains('dx-command-select')) {
          node.style.cssText = node.dataset.originalStyle;
        }
      });
    }
    if (rowElement?.[1]) {
      const rowHtmlElement = rowElement[1] as HTMLElement;
      // Reapply the original styles
      rowHtmlElement.style.cssText = '';

      rowHtmlElement?.childNodes?.forEach((node: any) => {
        if (!node.classList?.contains('dx-command-select')) {
          node.style.cssText = node.dataset.originalStyle;
        }
      });

      const checkboxContainer = rowHtmlElement.querySelector('div.dx-checkbox');
      if (checkboxContainer) {
        const checkbox = checkboxContainer.children[1].firstChild as HTMLElement;
        checkbox.style.cssText = '';
      }
    }
  });
};

/**
 * Updates the style of the select all checkbox in a dxDataGrid component based on its current state.
 * @param grid - The dxDataGrid component to update.
 * @param theme - The theme to use for styling.
 */
export const handleSelectAllCheckboxStyle = (grid: dxDataGrid<any, any> | undefined, theme: Theme) => {
  // Find the select all checkbox
  const selectAllCheckboxContainer: HTMLElement = grid
    ?.element()
    .querySelector('.dx-header-row .dx-checkbox') as HTMLElement;

  if (selectAllCheckboxContainer) {
    setTimeout(() => {
      const checkbox = selectAllCheckboxContainer?.children[1]?.firstChild as HTMLElement;
      if (!checkbox) return;

      if (
        selectAllCheckboxContainer.classList.contains('dx-checkbox-checked') ||
        selectAllCheckboxContainer.classList.contains('dx-checkbox-indeterminate')
      ) {
        checkbox.style.backgroundColor = theme.palette.customColors.primaryGradient;
      } else {
        checkbox.style.cssText = '';
      }
    }, 0);
  }

  // Find all checkboxes in the selection column
  const allCheckboxes: NodeListOf<HTMLElement> = grid
    ?.element()
    .querySelectorAll('.dx-command-select .dx-checkbox') as NodeListOf<HTMLElement>;

  allCheckboxes?.forEach(checkboxContainer => {
    const checkbox = checkboxContainer?.children[1]?.firstChild as HTMLElement;
    if (checkbox) {
      const isChecked =
        checkboxContainer.classList.contains('dx-checkbox-checked') ||
        checkboxContainer.classList.contains('dx-checkbox-indeterminate');

      if (isChecked) {
        checkbox.style.setProperty('background-color', theme.palette.customColors.primaryGradient, 'important');
      } else {
        checkbox.style.setProperty('background-color', '', 'important');
      }
    }
  });
};
